/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
  T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
export type ActivityListInput = {
  contactId: number;
  page?: number | null | undefined;
  perPage?: number | null | undefined;
  type: string;
};

export type AttachMeetingRecordingInput = {
  meetingId: string | number;
  recording: MeetingRecordingInput;
};

export type AuthorizationLinkInput = {
  codeChallenge: string;
  codeChallengeMethod: string;
  platform: Platform;
};

export type ClaimHandoffInput = {
  handoffId: string;
};

export type ContactListInput = {
  contactStatusId?: number | null | undefined;
  isArchived?: boolean | null | undefined;
  orderBy?: string | null | undefined;
  page?: number | null | undefined;
  perPage?: number | null | undefined;
  personInChargeId?: number | null | undefined;
  search?: string | null | undefined;
};

export type CreateMeetingInput = {
  contactIds?: Array<string | number> | null | undefined;
  endsAt?: string | null | undefined;
  location?: MeetingLocationInput | null | undefined;
  recording?: MeetingRecordingInput | null | undefined;
  startsAt: string;
  status?: MeetingStatus | null | undefined;
};

export type DevicePlatform = 'ANDROID' | 'IOS' | 'WEB';

export type ExchangeAuthorizationCodeInput = {
  code: string;
  codeVerifier?: string | null | undefined;
  state: string;
};

export type ExchangeRefreshTokenInput = {
  refreshToken: string;
};

export type GenerateContactFileDownloadUrlInput = {
  fileId: string;
};

export type GenerateContactFileUploadUrlInput = {
  name: string;
};

export type GenerateRecordingUploadUrlInput = {
  fileExtension: string;
  uuid: string;
};

export type HandoffListInput = {
  contactId?: string | null | undefined;
  createdAtFrom?: string | null | undefined;
  createdAtTo?: string | null | undefined;
  orderBy?: HandoffOrderBy | null | undefined;
  page?: number | null | undefined;
  perPage?: number | null | undefined;
  status?: HandoffStatus | null | undefined;
  userUid?: string | null | undefined;
};

export type HandoffOrderBy = 'CREATED_AT_DESC' | 'SEVERITY_DESC';

export type HandoffSeverity = 'IMPORTANT' | 'MODERATELY_IMPORTANT' | 'UNSPECIFIED' | 'URGENT';

export type HandoffSource = 'AGENT' | 'BPO_STAFF';

export type HandoffStatus = 'CLAIMED' | 'COMPLETED' | 'OPEN' | 'SKIPPED';

export type HandoffType =
  | 'MANUAL_PHONE_CALL'
  | 'MEETING_APPOINTMENT_SECURED'
  | 'PRINT_MATERIALS_SENT_MAIL'
  | 'PROPERTY_AVAILABILITY_CHECK'
  | 'SCHEDULE_MEETING_APPOINTMENT'
  | 'SEND_DOCUMENTS';

export type MeetingListInput = {
  contactId?: string | number | null | undefined;
  page?: number | null | undefined;
  perPage?: number | null | undefined;
  userUid?: string | null | undefined;
};

export type MeetingLocationInput = {
  address: string;
  latitude: number;
  longitude: number;
};

export type MeetingRecordingInput = {
  durationSeconds?: number | null | undefined;
  fileExtension: string;
  fileSizeBytes?: number | null | undefined;
  storagePath: string;
  uuid: string;
};

export type MeetingRecordingSummaryStatus =
  'COMPLETED' | 'FAILED' | 'ON_HOLD' | 'PENDING' | 'PROCESSING';

export type MeetingStatus = 'CANCELED' | 'COMPLETED' | 'MISSED' | 'SCHEDULED';

export type MeetingSummaryQuotaType = 'ACCOUNT' | 'USER';

export type NotificationDispatchStatus = 'DELIVERED' | 'READ';

export type Platform = 'NATIVE' | 'WEB';

export type RegisterDeviceInput = {
  platform: DevicePlatform;
  token: string;
};

export type ReleaseHandoffInput = {
  handoffId: string;
};

export type SendSmsHandoffInput = {
  handoffId: string;
  messageBody: string;
};

export type SmsChatInput = {
  contactId: string;
  page?: number | null | undefined;
  perPage?: number | null | undefined;
};

export type SmsChatMessageDirection = 'INBOUND' | 'OUTBOUND';

export type SmsChatSenderType = 'CONTACT' | 'USER';

export type UnregisterDeviceInput = {
  token: string;
};

export type UpdateContactInput = {
  contactStatusId?: number | null | undefined;
  id: number;
  personInChargeId?: number | null | undefined;
};

export type UpdateMeetingInput = {
  attendeeContactIds?: Array<string | number> | null | undefined;
  endsAt?: string | null | undefined;
  id: string | number;
  location?: MeetingLocationInput | null | undefined;
  name?: string | null | undefined;
  notes?: string | null | undefined;
  startsAt?: string | null | undefined;
  status?: MeetingStatus | null | undefined;
  userId?: string | number | null | undefined;
};

export type UpdateNotificationDispatchInput = {
  status: NotificationDispatchStatus;
  userDispatchId: string;
};

/**
 * Urgent handoffs are unassigned and have been waiting longer than the urgency window
 * (24 hours). Both conditions are applied by the BFF, so this input carries neither a
 * userUid filter nor the age threshold. Any createdAt range given here narrows the
 * result further; it never widens it past the threshold.
 */
export type UrgentHandoffListInput = {
  contactId?: string | null | undefined;
  createdAtFrom?: string | null | undefined;
  createdAtTo?: string | null | undefined;
  page?: number | null | undefined;
  perPage?: number | null | undefined;
};

export type GetFeatureSubscriptionsQueryVariables = Exact<{ [key: string]: never }>;

export type GetFeatureSubscriptionsQuery = { featureSubscriptions: { cvr: boolean } };

export type GetActivitiesQueryVariables = Exact<{
  input: ActivityListInput;
}>;

export type GetActivitiesQuery = {
  activities: {
    data: Array<{
      id: number | null;
      type: string | null;
      occurredAt: string | null;
      createdAt: string | null;
      updatedAt: string | null;
      meta: unknown;
      instigatorId: number | null;
      instigatorType: string | null;
      meeting: {
        id: string;
        name: string | null;
        startsAt: string;
        notes: string | null;
        attendees: Array<{
          id: string;
          type: string;
          attendeeId: string;
          contact: { displayName: string } | null;
        }>;
        recording: {
          id: string;
          summary: { status: MeetingRecordingSummaryStatus; summaryText: string | null } | null;
        } | null;
        user: { id: number; uid: string; name: string } | null;
      } | null;
    }>;
    pagination: {
      count: number;
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    } | null;
  };
};

export type GenerateContactFileUploadUrlMutationVariables = Exact<{
  input: GenerateContactFileUploadUrlInput;
}>;

export type GenerateContactFileUploadUrlMutation = {
  generateContactFileUploadUrl: {
    uploadUrl: string;
    path: string;
    headers: Array<{ name: string; values: Array<string> }>;
  };
};

export type GenerateContactFileDownloadUrlMutationVariables = Exact<{
  input: GenerateContactFileDownloadUrlInput;
}>;

export type GenerateContactFileDownloadUrlMutation = {
  generateContactFileDownloadUrl: { url: string };
};

export type GetContactStatusesQueryVariables = Exact<{ [key: string]: never }>;

export type GetContactStatusesQuery = {
  contactStatuses: { data: Array<{ id: number; name: string | null; color: string | null }> };
};

export type GetContactsQueryVariables = Exact<{
  input?: ContactListInput | null | undefined;
}>;

export type GetContactsQuery = {
  contacts: {
    data: Array<{
      id: number;
      displayName: string;
      fullName: string | null;
      email: string | null;
      notes: string | null;
      personalMobileNumber: string | null;
      contactStatusId: number | null;
      lastActionType: string | null;
      lastActionOccurredAt: string | null;
    }>;
    pagination: {
      count: number;
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    } | null;
  };
};

export type GetContactQueryVariables = Exact<{
  id: number;
}>;

export type GetContactQuery = {
  contact: {
    id: number;
    uid: string;
    firstName: string | null;
    firstNameKana: string | null;
    lastName: string | null;
    lastNameKana: string | null;
    fullName: string | null;
    email: string | null;
    age: string | null;
    gender: string | null;
    personalMobileNumber: string | null;
    personalMobileNumberCountryCode: string | null;
    personalLandlineNumber: string | null;
    personalLandlineNumberCountryCode: string | null;
    personalFaxNumber: string | null;
    personalFaxNumberCountryCode: string | null;
    personalCountry: string | null;
    personalRegion: string | null;
    personalCity: string | null;
    personalPostalCode: string | null;
    personalAddress1: string | null;
    personalAddress2: string | null;
    personalWebsiteUrl: string | null;
    workCompanyName: string | null;
    workWebsiteUrl: string | null;
    workDepartment: string | null;
    workJobTitle: string | null;
    workLandlineNumber: string | null;
    workLandlineNumberCountryCode: string | null;
    workMobileNumber: string | null;
    workMobileNumberCountryCode: string | null;
    workFaxNumber: string | null;
    workFaxNumberCountryCode: string | null;
    workCountry: string | null;
    workRegion: string | null;
    workCity: string | null;
    workPostalCode: string | null;
    workAddress1: string | null;
    workAddress2: string | null;
    notes: string | null;
    companyId: number | null;
    contactStatusId: number | null;
    lastActionType: string | null;
    lastActionOccurredAt: string | null;
    personInChargeId: number | null;
    firstApproachedAt: string | null;
    lastApproachedAt: string | null;
    creationMethod: string | null;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
    customerTypeNames: Array<string>;
    leadAcquisitionChannelNames: Array<string>;
    personInCharge: { id: number; uid: string; name: string; avatarUrl: string | null } | null;
    realEstateInquiry: {
      clientSelectedPlan: string | null;
      inquiredAt: string | null;
      preferredModeOfCommunication: string | null;
      preferredTimeOfCommunication: string | null;
      content: string | null;
      yearlyIncome: string | null;
    } | null;
    realEstateConstructionInquiry: {
      locationDetails: string | null;
      plannedTiming: string | null;
      budget: string | null;
      requirements: string | null;
      type: string | null;
      modelHouseName: string | null;
      reservationTiming: string | null;
      eventName: string | null;
      eventTiming: string | null;
      preferredCatalogName: string | null;
      exhibitionVenueName: string | null;
    } | null;
    realEstatePurchaseInquiry: {
      propertyType: string | null;
      propertyName: string | null;
      preferredMoveInDate: string | null;
      propertyImageUrl: string | null;
      preferredPropertySpecification: string | null;
      preferredArea: string | null;
    } | null;
    realEstateSaleInquiry: {
      propertyType: string | null;
      propertyAddress: string | null;
      propertyLandArea: string | null;
      propertyBuildingArea: string | null;
      propertyFloorPlan: string | null;
      propertyAge: string | null;
      propertyVacancyStatus: string | null;
      propertyOwnerName: string | null;
      preferredSaleTime: string | null;
      saleReason: string | null;
      preferredAssessmentMethod: string | null;
      offerPrice: string | null;
      requests: string | null;
    } | null;
    customFields: Array<{ code: string; label: string; type: string; value: string | null }>;
  };
};

export type UpdateContactMutationVariables = Exact<{
  input: UpdateContactInput;
}>;

export type UpdateContactMutation = {
  updateContact: { id: number; contactStatusId: number | null; personInChargeId: number | null };
};

export type RegisterDeviceMutationVariables = Exact<{
  input: RegisterDeviceInput;
}>;

export type RegisterDeviceMutation = { registerDevice: boolean };

export type UnregisterDeviceMutationVariables = Exact<{
  input: UnregisterDeviceInput;
}>;

export type UnregisterDeviceMutation = { unregisterDevice: boolean };

export type GetHandoffUnreadStateQueryVariables = Exact<{ [key: string]: never }>;

export type GetHandoffUnreadStateQuery = {
  handoffUnreadState: { totalCount: number; contactIds: Array<string>; createdAts: Array<string> };
};

export type UpdateNotificationDispatchMutationVariables = Exact<{
  input: UpdateNotificationDispatchInput;
}>;

export type UpdateNotificationDispatchMutation = { updateNotificationDispatch: boolean };

export type GetHandoffsQueryVariables = Exact<{
  input?: HandoffListInput | null | undefined;
}>;

export type GetHandoffsQuery = {
  handoffs: {
    data: Array<{
      id: string;
      contactId: string | null;
      handoffType: HandoffType;
      status: HandoffStatus;
      severity: HandoffSeverity;
      userUid: string | null;
      source: HandoffSource;
      notes: string | null;
      claimedAt: string | null;
      completedAt: string | null;
      createdAt: string;
      updatedAt: string;
      skippedAt: string | null;
      contact: { id: number; fullName: string | null } | null;
      unreadDispatch: { id: string; dispatchedAt: string | null } | null;
    }>;
    pagination: {
      count: number;
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    } | null;
  };
};

export type GetUrgentHandoffsQueryVariables = Exact<{
  input?: UrgentHandoffListInput | null | undefined;
}>;

export type GetUrgentHandoffsQuery = {
  urgentHandoffs: {
    data: Array<{
      id: string;
      contactId: string | null;
      handoffType: HandoffType;
      status: HandoffStatus;
      severity: HandoffSeverity;
      userUid: string | null;
      source: HandoffSource;
      notes: string | null;
      claimedAt: string | null;
      completedAt: string | null;
      createdAt: string;
      updatedAt: string;
      skippedAt: string | null;
      contact: { id: number; fullName: string | null } | null;
      unreadDispatch: { id: string; dispatchedAt: string | null } | null;
    }>;
    pagination: {
      count: number;
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    } | null;
  };
};

export type GetHandoffQueryVariables = Exact<{
  id: string;
}>;

export type GetHandoffQuery = {
  handoff: {
    id: string;
    contactId: string | null;
    handoffType: HandoffType;
    status: HandoffStatus;
    severity: HandoffSeverity;
    userUid: string | null;
    source: HandoffSource;
    notes: string | null;
    claimedAt: string | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
    skippedAt: string | null;
    unreadDispatch: { id: string; dispatchedAt: string | null } | null;
  };
};

export type ClaimHandoffMutationVariables = Exact<{
  input: ClaimHandoffInput;
}>;

export type ClaimHandoffMutation = {
  claimHandoff: {
    id: string;
    status: HandoffStatus;
    userUid: string | null;
    claimedAt: string | null;
    updatedAt: string;
  };
};

export type ReleaseHandoffMutationVariables = Exact<{
  input: ReleaseHandoffInput;
}>;

export type ReleaseHandoffMutation = {
  releaseHandoff: {
    id: string;
    status: HandoffStatus;
    userUid: string | null;
    claimedAt: string | null;
    updatedAt: string;
  };
};

export type GenerateRecordingUploadUrlMutationVariables = Exact<{
  input: GenerateRecordingUploadUrlInput;
}>;

export type GenerateRecordingUploadUrlMutation = {
  generateRecordingUploadUrl: {
    uploadUrl: string;
    storagePath: string;
    headers: Array<{ name: string; values: Array<string> }>;
  };
};

export type CreateMeetingMutationVariables = Exact<{
  input: CreateMeetingInput;
}>;

export type CreateMeetingMutation = {
  createMeeting: {
    id: string;
    startsAt: string;
    endsAt: string | null;
    recording: {
      id: string;
      uuid: string;
      durationSeconds: number | null;
      fileSizeBytes: number | null;
      summary: { status: MeetingRecordingSummaryStatus } | null;
    } | null;
  };
};

export type GetMeetingQueryVariables = Exact<{
  id: string | number;
}>;

export type GetMeetingQuery = {
  meeting: {
    id: string;
    name: string | null;
    status: MeetingStatus;
    startsAt: string;
    endsAt: string | null;
    notes: string | null;
    location: { address: string; latitude: number; longitude: number } | null;
    attendees: Array<{
      id: string;
      type: string;
      attendeeId: string;
      contact: { displayName: string } | null;
    }>;
    recording: {
      id: string;
      uuid: string;
      durationSeconds: number | null;
      fileSizeBytes: number | null;
      summary: {
        status: MeetingRecordingSummaryStatus;
        summaryText: string | null;
        topics: Array<string> | null;
        completedAt: string | null;
      } | null;
    } | null;
    user: { id: number; uid: string; name: string; avatarUrl: string | null } | null;
  };
};

export type GetMeetingsQueryVariables = Exact<{
  input?: MeetingListInput | null | undefined;
}>;

export type GetMeetingsQuery = {
  meetings: {
    data: Array<{
      id: string;
      name: string | null;
      status: MeetingStatus;
      startsAt: string;
      endsAt: string | null;
      location: { address: string } | null;
      attendees: Array<{
        id: string;
        type: string;
        attendeeId: string;
        contact: { displayName: string } | null;
      }>;
      recording: {
        id: string;
        uuid: string;
        durationSeconds: number | null;
        summary: { status: MeetingRecordingSummaryStatus } | null;
      } | null;
    }>;
    pagination: {
      total: number;
      count: number;
      perPage: number;
      currentPage: number;
      totalPages: number;
    } | null;
  };
};

export type AttachMeetingRecordingMutationVariables = Exact<{
  input: AttachMeetingRecordingInput;
}>;

export type AttachMeetingRecordingMutation = {
  attachMeetingRecording: {
    id: string;
    recording: {
      id: string;
      uuid: string;
      durationSeconds: number | null;
      fileSizeBytes: number | null;
      summary: { status: MeetingRecordingSummaryStatus } | null;
    } | null;
  };
};

export type UpdateMeetingMutationVariables = Exact<{
  input: UpdateMeetingInput;
}>;

export type UpdateMeetingMutation = {
  updateMeeting: {
    id: string;
    name: string | null;
    notes: string | null;
    attendees: Array<{
      id: string;
      type: string;
      attendeeId: string;
      contact: { displayName: string } | null;
    }>;
    user: { id: number; uid: string; name: string; avatarUrl: string | null } | null;
  };
};

export type MeetingSummaryQuotaUsageQueryVariables = Exact<{ [key: string]: never }>;

export type MeetingSummaryQuotaUsageQuery = {
  meetingSummaryQuotaUsage: {
    usedMinutes: number;
    totalMinutes: number;
    quotas: Array<{ type: MeetingSummaryQuotaType; allowanceMinutes: number; usedMinutes: number }>;
  };
};

export type GetAuthorizationLinkQueryVariables = Exact<{
  input: AuthorizationLinkInput;
}>;

export type GetAuthorizationLinkQuery = {
  authorizationLink: { redirectUrl: string; state: string };
};

export type ExchangeAuthorizationCodeMutationVariables = Exact<{
  input: ExchangeAuthorizationCodeInput;
}>;

export type ExchangeAuthorizationCodeMutation = {
  exchangeAuthorizationCode: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    refreshTokenExpiresIn: number;
    tokenType: string;
  };
};

export type ExchangeRefreshTokenMutationVariables = Exact<{
  input: ExchangeRefreshTokenInput;
}>;

export type ExchangeRefreshTokenMutation = {
  exchangeRefreshToken: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    refreshTokenExpiresIn: number;
    tokenType: string;
  };
};

export type RevokeAccessTokenMutationVariables = Exact<{ [key: string]: never }>;

export type RevokeAccessTokenMutation = { revokeAccessToken: boolean };

export type GetContactProfileAttributesOrderQueryVariables = Exact<{ [key: string]: never }>;

export type GetContactProfileAttributesOrderQuery = {
  contactProfileAttributesOrder: Array<{
    field: string;
    name: string;
    type: string | null;
    label: string | null;
  }>;
};

export type GetSmsChatQueryVariables = Exact<{
  input: SmsChatInput;
}>;

export type GetSmsChatQuery = {
  smsChat: {
    data: Array<{
      id: string;
      messageId: string | null;
      body: string;
      direction: SmsChatMessageDirection;
      phoneNumber: string | null;
      phoneNumberType: string | null;
      sentAt: string;
      sender: { type: SmsChatSenderType; name: string | null };
    }>;
    pagination: {
      count: number;
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
  };
};

export type SendSmsHandoffMutationVariables = Exact<{
  input: SendSmsHandoffInput;
}>;

export type SendSmsHandoffMutation = {
  sendSmsHandoff: {
    messageId: string;
    messageStatus: string;
    handoffId: string;
    handoffStatus: string;
  };
};

export type MeQueryVariables = Exact<{ [key: string]: never }>;

export type MeQuery = {
  me: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string;
    role: string;
    accountId: string;
    backendAppUserId: string;
    backendAppUserUid: string;
  };
};

export type GetUsersQueryVariables = Exact<{
  isEnabled?: boolean | null | undefined;
  isActivated?: boolean | null | undefined;
}>;

export type GetUsersQuery = {
  users: { data: Array<{ id: number; uid: string; name: string; avatarUrl: string | null }> };
};

export const GetFeatureSubscriptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetFeatureSubscriptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'featureSubscriptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'cvr' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetFeatureSubscriptionsQuery, GetFeatureSubscriptionsQueryVariables>;
export const GetActivitiesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetActivities' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ActivityListInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activities' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'occurredAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'meta' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'instigatorId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'instigatorType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'meeting' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'startsAt' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'attendees' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'attendeeId' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'contact' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'displayName' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'recording' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'summary' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'summaryText' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'user' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'uid' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pagination' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currentPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'perPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalPages' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetActivitiesQuery, GetActivitiesQueryVariables>;
export const GenerateContactFileUploadUrlDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'GenerateContactFileUploadUrl' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'GenerateContactFileUploadUrlInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'generateContactFileUploadUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'uploadUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'path' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'headers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'values' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  GenerateContactFileUploadUrlMutation,
  GenerateContactFileUploadUrlMutationVariables
>;
export const GenerateContactFileDownloadUrlDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'GenerateContactFileDownloadUrl' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'GenerateContactFileDownloadUrlInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'generateContactFileDownloadUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  GenerateContactFileDownloadUrlMutation,
  GenerateContactFileDownloadUrlMutationVariables
>;
export const GetContactStatusesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetContactStatuses' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactStatuses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetContactStatusesQuery, GetContactStatusesQueryVariables>;
export const GetContactsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetContacts' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ContactListInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contacts' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'personalMobileNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'contactStatusId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastActionType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastActionOccurredAt' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pagination' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currentPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'perPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalPages' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetContactsQuery, GetContactsQueryVariables>;
export const GetContactDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetContact' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contact' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'uid' } },
                { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'firstNameKana' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lastNameKana' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'age' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalMobileNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalMobileNumberCountryCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalLandlineNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'personalLandlineNumberCountryCode' },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'personalFaxNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalFaxNumberCountryCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalCountry' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalRegion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalCity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalPostalCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalAddress1' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalAddress2' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personalWebsiteUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workCompanyName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workWebsiteUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workDepartment' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workJobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workLandlineNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workLandlineNumberCountryCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workMobileNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workMobileNumberCountryCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workFaxNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workFaxNumberCountryCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workCountry' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workRegion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workCity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workPostalCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workAddress1' } },
                { kind: 'Field', name: { kind: 'Name', value: 'workAddress2' } },
                { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contactStatusId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lastActionType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lastActionOccurredAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personInChargeId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'personInCharge' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'uid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'avatarUrl' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'firstApproachedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lastApproachedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'creationMethod' } },
                { kind: 'Field', name: { kind: 'Name', value: 'archivedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerTypeNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'leadAcquisitionChannelNames' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'realEstateInquiry' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'clientSelectedPlan' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'inquiredAt' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'preferredModeOfCommunication' },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'preferredTimeOfCommunication' },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'content' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'yearlyIncome' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'realEstateConstructionInquiry' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'locationDetails' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'plannedTiming' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'budget' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'requirements' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'modelHouseName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'reservationTiming' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventTiming' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'preferredCatalogName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'exhibitionVenueName' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'realEstatePurchaseInquiry' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'preferredMoveInDate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyImageUrl' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'preferredPropertySpecification' },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'preferredArea' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'realEstateSaleInquiry' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyAddress' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyLandArea' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyBuildingArea' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyFloorPlan' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyAge' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyVacancyStatus' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'propertyOwnerName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'preferredSaleTime' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'saleReason' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'preferredAssessmentMethod' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'offerPrice' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'requests' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customFields' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetContactQuery, GetContactQueryVariables>;
export const UpdateContactDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UpdateContact' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UpdateContactInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateContact' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contactStatusId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'personInChargeId' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateContactMutation, UpdateContactMutationVariables>;
export const RegisterDeviceDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RegisterDevice' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'RegisterDeviceInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registerDevice' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RegisterDeviceMutation, RegisterDeviceMutationVariables>;
export const UnregisterDeviceDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UnregisterDevice' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UnregisterDeviceInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unregisterDevice' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UnregisterDeviceMutation, UnregisterDeviceMutationVariables>;
export const GetHandoffUnreadStateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetHandoffUnreadState' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'handoffUnreadState' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contactIds' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAts' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetHandoffUnreadStateQuery, GetHandoffUnreadStateQueryVariables>;
export const UpdateNotificationDispatchDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UpdateNotificationDispatch' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'UpdateNotificationDispatchInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateNotificationDispatch' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpdateNotificationDispatchMutation,
  UpdateNotificationDispatchMutationVariables
>;
export const GetHandoffsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetHandoffs' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'HandoffListInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'handoffs' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'contactId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'handoffType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'severity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'userUid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'source' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'claimedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'completedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'skippedAt' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'contact' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unreadDispatch' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'dispatchedAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pagination' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currentPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'perPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalPages' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetHandoffsQuery, GetHandoffsQueryVariables>;
export const GetUrgentHandoffsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetUrgentHandoffs' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'UrgentHandoffListInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'urgentHandoffs' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'contactId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'handoffType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'severity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'userUid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'source' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'claimedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'completedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'skippedAt' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'contact' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unreadDispatch' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'dispatchedAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pagination' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currentPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'perPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalPages' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetUrgentHandoffsQuery, GetUrgentHandoffsQueryVariables>;
export const GetHandoffDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetHandoff' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Uint' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'handoff' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contactId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'handoffType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'severity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'userUid' } },
                { kind: 'Field', name: { kind: 'Name', value: 'source' } },
                { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'claimedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'completedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'skippedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'unreadDispatch' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'dispatchedAt' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetHandoffQuery, GetHandoffQueryVariables>;
export const ClaimHandoffDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ClaimHandoff' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ClaimHandoffInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'claimHandoff' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'userUid' } },
                { kind: 'Field', name: { kind: 'Name', value: 'claimedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ClaimHandoffMutation, ClaimHandoffMutationVariables>;
export const ReleaseHandoffDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ReleaseHandoff' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReleaseHandoffInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'releaseHandoff' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'userUid' } },
                { kind: 'Field', name: { kind: 'Name', value: 'claimedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReleaseHandoffMutation, ReleaseHandoffMutationVariables>;
export const GenerateRecordingUploadUrlDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'GenerateRecordingUploadUrl' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'GenerateRecordingUploadUrlInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'generateRecordingUploadUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'uploadUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storagePath' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'headers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'values' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  GenerateRecordingUploadUrlMutation,
  GenerateRecordingUploadUrlMutationVariables
>;
export const CreateMeetingDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CreateMeeting' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateMeetingInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createMeeting' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'startsAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'endsAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'recording' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'uuid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'durationSeconds' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fileSizeBytes' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'summary' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'status' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateMeetingMutation, CreateMeetingMutationVariables>;
export const GetMeetingDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetMeeting' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'meeting' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'startsAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'endsAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'location' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'address' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'latitude' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'longitude' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'attendees' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'attendeeId' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'contact' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'recording' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'uuid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'durationSeconds' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fileSizeBytes' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'summary' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'summaryText' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'topics' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'completedAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'uid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'avatarUrl' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetMeetingQuery, GetMeetingQueryVariables>;
export const GetMeetingsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetMeetings' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MeetingListInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'meetings' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startsAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endsAt' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'location' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'address' } }],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'attendees' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'attendeeId' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contact' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'recording' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'uuid' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'durationSeconds' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'summary' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pagination' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'perPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currentPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalPages' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetMeetingsQuery, GetMeetingsQueryVariables>;
export const AttachMeetingRecordingDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AttachMeetingRecording' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'AttachMeetingRecordingInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'attachMeetingRecording' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'recording' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'uuid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'durationSeconds' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fileSizeBytes' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'summary' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'status' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AttachMeetingRecordingMutation,
  AttachMeetingRecordingMutationVariables
>;
export const UpdateMeetingDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UpdateMeeting' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UpdateMeetingInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateMeeting' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'attendees' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'attendeeId' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'contact' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'uid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'avatarUrl' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateMeetingMutation, UpdateMeetingMutationVariables>;
export const MeetingSummaryQuotaUsageDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MeetingSummaryQuotaUsage' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'meetingSummaryQuotaUsage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'quotas' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'allowanceMinutes' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'usedMinutes' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'usedMinutes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'totalMinutes' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MeetingSummaryQuotaUsageQuery, MeetingSummaryQuotaUsageQueryVariables>;
export const GetAuthorizationLinkDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetAuthorizationLink' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AuthorizationLinkInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'authorizationLink' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'redirectUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'state' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetAuthorizationLinkQuery, GetAuthorizationLinkQueryVariables>;
export const ExchangeAuthorizationCodeDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ExchangeAuthorizationCode' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'ExchangeAuthorizationCodeInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'exchangeAuthorizationCode' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'accessToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refreshToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'expiresIn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refreshTokenExpiresIn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tokenType' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ExchangeAuthorizationCodeMutation,
  ExchangeAuthorizationCodeMutationVariables
>;
export const ExchangeRefreshTokenDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ExchangeRefreshToken' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ExchangeRefreshTokenInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'exchangeRefreshToken' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'accessToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refreshToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'expiresIn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refreshTokenExpiresIn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tokenType' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ExchangeRefreshTokenMutation, ExchangeRefreshTokenMutationVariables>;
export const RevokeAccessTokenDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RevokeAccessToken' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [{ kind: 'Field', name: { kind: 'Name', value: 'revokeAccessToken' } }],
      },
    },
  ],
} as unknown as DocumentNode<RevokeAccessTokenMutation, RevokeAccessTokenMutationVariables>;
export const GetContactProfileAttributesOrderDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetContactProfileAttributesOrder' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactProfileAttributesOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'field' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'label' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  GetContactProfileAttributesOrderQuery,
  GetContactProfileAttributesOrderQueryVariables
>;
export const GetSmsChatDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetSmsChat' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SmsChatInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'smsChat' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'messageId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'body' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'sender' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sentAt' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pagination' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currentPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'perPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalPages' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetSmsChatQuery, GetSmsChatQueryVariables>;
export const SendSmsHandoffDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'SendSmsHandoff' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SendSmsHandoffInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sendSmsHandoff' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'messageId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'messageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'handoffId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'handoffStatus' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SendSmsHandoffMutation, SendSmsHandoffMutationVariables>;
export const MeDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'Me' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'me' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'avatarUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'backendAppUserId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'backendAppUserUid' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MeQuery, MeQueryVariables>;
export const GetUsersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetUsers' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'isEnabled' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'isActivated' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'isEnabled' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'isEnabled' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'isActivated' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'isActivated' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'uid' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'avatarUrl' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<GetUsersQuery, GetUsersQueryVariables>;
